import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFoods, FoodDto, PagedResult } from '../services/foods';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Pagination from '@mui/material/Pagination';
import InputAdornment from '@mui/material/InputAdornment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';

const PAGE_SIZE = 12;

export default function Foods() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get URL parameters
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const searchQuery = searchParams.get('search') || '';
    
    const [pagedResult, setPagedResult] = useState<PagedResult<FoodDto> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState(searchQuery);

    const fetchFoods = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getFoods({
                page: currentPage,
                pageSize: PAGE_SIZE,
                search: searchQuery || undefined,
            });
            
            // Normalize potential $values array
            const normalizedItems = Array.isArray(result.items)
                ? result.items
                : Array.isArray((result.items as unknown as { $values?: FoodDto[] })?.$values)
                    ? (result.items as unknown as { $values?: FoodDto[] }).$values!
                    : [];
            
            setPagedResult({
                ...result,
                items: normalizedItems,
            });
        } catch (err) {
            console.error('Error fetching foods:', err);
            setError('Failed to load foods. Please make sure you are logged in.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery]);

    useEffect(() => {
        fetchFoods();
    }, [fetchFoods]);

    // Sync search input with URL when URL changes
    useEffect(() => {
        setSearchInput(searchQuery);
    }, [searchQuery]);

    const handleFoodClick = (foodId: string) => {
        navigate(`/foods/${foodId}`);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', page.toString());
        setSearchParams(newParams);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newParams = new URLSearchParams();
        if (searchInput.trim()) {
            newParams.set('search', searchInput.trim());
        }
        newParams.set('page', '1'); // Reset to first page on new search
        setSearchParams(newParams);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        const newParams = new URLSearchParams();
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box m={2}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const foods = pagedResult?.items || [];
    const totalPages = pagedResult?.totalPages || 0;
    const totalCount = pagedResult?.totalCount || 0;

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">
                    Recepty
                </Typography>
                <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Hledat recepty..."
                        value={searchInput}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 250 }}
                    />
                    {searchQuery && (
                        <Chip 
                            label={`"${searchQuery}"`}
                            onDelete={handleClearSearch}
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Box>

            {searchQuery && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nalezeno {totalCount} {totalCount === 1 ? 'recept' : totalCount < 5 ? 'recepty' : 'receptů'} pro "{searchQuery}"
                </Typography>
            )}

            <Grid container spacing={3}>
                {foods.map((food) => (
                    <Grid item xs={12} sm={6} md={4} key={food.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardActionArea 
                                onClick={() => handleFoodClick(food.id)}
                                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                            >
                                {food.images && food.images.length > 0 && (
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={food.images[0].path}
                                        alt={food.name}
                                    />
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography gutterBottom variant="h5" component="div">
                                        {food.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                {food.durationMinutes} min
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PeopleIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                {food.peopleCount}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {food.categoryNames && food.categoryNames.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                            {food.categoryNames.map((category, index) => (
                                                <Chip key={index} label={category} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {(food.recipe || '').substring(0, 100)}
                                        {(food.recipe || '').length > 100 ? '...' : ''}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {foods.length === 0 && (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                    {searchQuery 
                        ? `Žádné recepty nebyly nalezeny pro "${searchQuery}".`
                        : 'Žádné recepty nebyly nalezeny.'}
                </Typography>
            )}

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
}